import React, { useState } from 'react';
import { Switch } from '../common/Catalyst/switch';
import { Text } from '../common/Catalyst/text';
import { hoaService } from '../clients/ApiClients';
import { apiErrorMessage } from '../utils/apiErrors';
import { ErrorAlert } from '../common/Custom/ErrorAlert';

interface Props {
  hoaId: string;
  /** From `HoaReturn.board_member_impersonation`. */
  enabled: boolean;
  /** Lets the parent keep its copy of the HOA in step. */
  onChanged?: (enabled: boolean) => void;
}

/**
 * Whether this association's **board members** may open a read-only view
 * of one of its homeowners' portals.
 *
 * Management company staff — Owner, Admin, Manager — are not governed by
 * this. They already hold this data in front-porch, they carry the
 * liability, and they are who the homeowner complains to. The switch
 * exists for the one role whose access is a judgement call rather than a
 * job requirement.
 *
 * **Defaults off, and the default is the point.** Impersonation is
 * read-only, so nothing it does can be undone wrongly — what cannot be
 * undone is the *reading*. A board member is a volunteer, frequently the
 * homeowner's neighbour, and the portal holds ledger balances, payment
 * history and violation records. Somebody who has seen a neighbour's
 * arrears cannot un-see them, and no later toggle helps.
 *
 * Only an Owner or Admin can change it. The board is the party this
 * governs, so letting a board member flip it would be the toggle
 * deciding itself — enforced server-side in
 * `can_set_board_impersonation`; this component is simply not rendered
 * for anyone else.
 */
const BoardImpersonationPanel: React.FC<Props> = ({
  hoaId,
  enabled,
  onChanged,
}) => {
  const [on, setOn] = useState(enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async (next: boolean) => {
    try {
      setSaving(true);
      setError(null);
      const { data } = await hoaService.setBoardImpersonation({
        id: hoaId,
        boardImpersonationUpdate: { enabled: next },
      });
      setOn(data.board_member_impersonation);
      onChanged?.(data.board_member_impersonation);
    } catch (err) {
      setError(
        apiErrorMessage(err, "Couldn't change this setting. Please try again.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden mb-4">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Text className="font-semibold">
              Let board members view a homeowner’s account
            </Text>
            <Text className="mt-1 text-sm">
              Board members of this association will be able to open a
              homeowner’s portal and see it exactly as that homeowner does —
              including their balance, payment history and violations. The
              view is read-only, lasts an hour, and every use is recorded.
            </Text>
            <Text className="mt-1 text-sm">
              Your own staff can already do this and are not affected by this
              setting.
            </Text>
          </div>
          <Switch
            checked={on}
            onChange={toggle}
            disabled={saving}
            aria-label="Let board members view a homeowner’s account"
          />
        </div>

        <ErrorAlert className="mt-3">{error}</ErrorAlert>
      </div>
    </div>
  );
};

export default BoardImpersonationPanel;
